import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { Components, LoadingOverlay } from '@oneamerica/dxp-ui-components';
import responsiveStyle from '../PremiumsReceived/style';
import InfoWrapper from '../../../../components/InfoWrapper/InfoWrapper';
import { LatestStatement } from '../../../../components/LatestStatements';
import { trackCustomEvent } from '../../../../utils/Analytics/AdobeAnalyticsUtils';
import { analyticsTags } from '../../../../utils/Analytics/AnalyticsTags';
import { useDispatch, useSelector } from 'react-redux';
import MakePaymentModal from './MakePaymentModal';
import { isHomeOfficeUser } from '../../../../utils';
import { PNC_SHO_URL } from '../../../../config/getEnvironmentVars';
import LegalDisclaimerModal from './LegalDisclaimerModal';
import { GET_PAYMENTS_DATA } from '../../../../redux/payments/types';
import { resetPaymentsEncryptedData } from '../../../../redux/payments/paymentsSlice';
import { ProcessingTroubleModal } from '../../../../components/ProcessingTroubleModal';

const BillingAndPremium = ({
  billingAndPremiumData,
  accessToken,
  policyNumber,
  account,
}) => {
  const style = responsiveStyle();
  const dispatch = useDispatch();

  const [isModalVisible, setModalVisible] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false); // Loading state to disable button during flow
  const [paymentData, setPaymentData] = useState<any>([]);

  const { ...policyData } = useSelector((state: any) => state.policies);
  const isHomeOfcUser = isHomeOfficeUser();
  const paymentEncryptedData = useSelector(
    (state: any) => state.paymentEncryptedData,
  );

  const showMakePaymentButton = useMemo(() => {
    return (
      policyData.getPoliciesListData?.data?.policySummary?.policyHighlights[0]
        ?.accountConfig?.allowPaymentButton === true
    );
  }, [policyData]);

  const generalPolicyData =
    policyData?.getPoliciesListData?.data?.policySummary?.policyHighlights?.[0]
      ?.generalPolicyData;
  const hasPendingPayment =
    policyData.getPoliciesListData?.data?.policySummary?.policyHighlights[0]
      ?.accountConfig?.hasPendingPayment;

  const formRef = useRef<HTMLFormElement | null>(null);
  const redirectShownRef = useRef(false);

  // --- SESSION STORAGE For Payment Status ---
  const getPolicySessionKey = (policyNumber) => `paymentStatus_${policyNumber}`;
  const getHandledKey = (policyNumber) =>
    `paymentStatusHandled_${policyNumber}`;

  useEffect(() => {
    const policyNumber = account?.number;

    const apiAccountNumber =
      policyData?.getPoliciesListData?.data?.policySummary?.account?.number;
    const apiHasPending =
      policyData?.getPoliciesListData?.data?.policySummary
        ?.policyHighlights?.[0]?.accountConfig?.hasPendingPayment;

    const key = getPolicySessionKey(policyNumber);
    const storedStatus = sessionStorage.getItem(key);

    const urlParams = new URLSearchParams(globalThis.location.search);
    const initialRedirectIsSuccess =
      urlParams.get('paymentStatus') === 'success';

    const handledKey = getHandledKey(policyNumber);
    const handled = sessionStorage.getItem(handledKey) === 'true';

    const isApiPendingForThisPolicy =
      Boolean(apiHasPending) && apiAccountNumber === policyNumber;

    if (initialRedirectIsSuccess && !handled && policyNumber) {
      redirectShownRef.current = true;
      setPaymentStatus('success');
      return;
    }

    if (isApiPendingForThisPolicy) {
      if (storedStatus === 'success' && redirectShownRef.current === true) {
        setPaymentStatus('success');
        return;
      }

      setPaymentStatus('pending');
      return;
    }

    if (storedStatus === 'success') setPaymentStatus('success');
    else setPaymentStatus(undefined);
  }, [account?.number, policyData]);

  const getPaymentTypes = () => {
    const generalPolicyData =
      policyData?.getPoliciesListData?.data?.policySummary
        ?.policyHighlights?.[0]?.generalPolicyData;
    const payments: { title: string; amount: number }[] = [];

    if (generalPolicyData?.billingAmount) {
      payments.push({
        title: 'Base Premium',
        amount: generalPolicyData?.billingAmount,
      });
    } else {
      payments.push({
        title: 'Base Premium',
        amount: 0,
      });
    }

    if (generalPolicyData?.billRiderAmt) {
      payments.push({
        title: 'Paid Up Addition',
        amount: generalPolicyData?.billRiderAmt,
      });
    } else {
      payments.push({
        title: 'Paid Up Addition',
        amount: 0,
      });
    }

    const filteredPayments = payments.filter((item) => item.amount > 0);
    return filteredPayments;
  };

  const shouldShowPendingBanner = hasPendingPayment === true;

  const basePremium = generalPolicyData?.billingAmount || 0;
  const paidUpAddition = generalPolicyData?.billRiderAmt || 0;
  const premiumDeposit = Number.parseFloat('0');

  const shouldDisablePaymentButton =
    (basePremium <= 0 && paidUpAddition <= 0 && premiumDeposit <= 0) ||
    paymentStatus === 'success';

  useEffect(() => {
    dispatch(resetPaymentsEncryptedData());
    setPaymentData(null);
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    if (paymentEncryptedData?.data || paymentEncryptedData?.error) {
      setIsLoading(false); // Re-enable button
    }
    if (paymentEncryptedData?.data) {
      setPaymentData(paymentEncryptedData.data);
    } else if (paymentEncryptedData?.error) {
      trackCustomEvent(
        analyticsTags.CapturePaymentDataApiFailurePolicyDetailsPage,
      );
      console.error('Error fetching payment data:', paymentEncryptedData.error);
      setPaymentData(null);
    }
  }, [paymentEncryptedData]);

  const onPressMakeAPayment = () => {
    setIsLoading(true);
    trackCustomEvent(analyticsTags.CaptureMakeAPaymentButtonPolicDetailsTags);

    const premiums = [
      basePremium > 0 ? 'BASE' : null,
      paidUpAddition > 0 ? 'PUAR' : null,
      premiumDeposit > 0 ? 'PDR' : null,
    ].filter(Boolean);

    if (premiums.length === 1) {
      const premiumType = premiums[0];

      let queryParam = '';
      if (premiumType === 'BASE') queryParam = 'P';
      else if (premiumType === 'PUAR') queryParam = 'U';
      // For BASE, queryParam stays 'P', For PUAR it stays 'U'

      dispatch({
        type: GET_PAYMENTS_DATA,
        payload: {
          token: policyData?.token,
          policyNumber:
            policyData?.getPoliciesListData?.data?.policySummary?.account
              ?.number,
          paymentTypes: queryParam || [],
          isLoading: true,
        },
      });
    } else {
      // Show modal popup if multiple or none are > 0
      setModalVisible(true);
      setIsLoading(false);
    }
  };

  const handleLegalDisclaimerAgree = () => {
    if (sessionStorage.getItem('pncCallbackRole')) {
      sessionStorage.removeItem('pncCallbackRole');
    }
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const sendAnalytics = () => {
    trackCustomEvent(
      analyticsTags.CaptureMakeAPaymentCloseButtonPolicyDetailsPageTags,
    );
  };

  const getSelectedPaymentsCodes = (selectedPayments): string[] => {
    const hasBase = selectedPayments.some(
      (p) => p.title?.toLowerCase() === 'base premium',
    );
    const hasPuar = selectedPayments.some(
      (p) => p.title?.toLowerCase() === 'paid up addition',
    );
    return [hasBase && 'P', hasPuar && 'U'].filter(Boolean) as string[];
  };

  const allowLatestStatement =
    policyData?.getPoliciesListData?.data?.policySummary?.policyHighlights?.[0]
      ?.accountConfig?.allowLatestStatement;

  return (
    <>
      <LoadingOverlay loading={isLoading} />
      <View className="lg:flex-row">
        <View className="w-full p-0 mt-8">
          <View style={style.cardShadow}>
            <View
              style={[
                style.cardHeader,
                {
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                },
              ]}
            >
              <Components.Text.H3 style={[style.cardHeaderTitle, style.flex]}>
                Billing & Premiums
              </Components.Text.H3>
              {showMakePaymentButton && (
                <>
                  <Components.Button
                    className="mx-2"
                    accessibilityRole="button"
                    id="btnOutline"
                    theme="primary"
                    disabled={
                      isHomeOfcUser ||
                      shouldDisablePaymentButton ||
                      shouldShowPendingBanner ||
                      isLoading
                    }
                    onPress={() => {
                      if (isHomeOfcUser || shouldDisablePaymentButton) return;
                      // insert payment logic here
                      onPressMakeAPayment();
                    }}
                    style={style.makeaPaymentButton}
                  >
                    Make this Payment
                  </Components.Button>
                  <form
                    ref={formRef}
                    action={PNC_SHO_URL}
                    method="post"
                    style={{ display: 'none' }}
                  >
                    <input
                      type="hidden"
                      name="BillTypeLabel"
                      value={paymentData?.billTypeLabel ?? ''}
                    />
                    <input
                      type="hidden"
                      name="CompanyName"
                      value={paymentData?.companyName ?? ''}
                    />
                    <input
                      type="hidden"
                      name="AmountDueLabel"
                      value={paymentData?.amountDueLabel ?? ''}
                    />
                    <input
                      type="hidden"
                      name="EmailAddress"
                      value={paymentData?.emailAddress ?? ''}
                    />
                    <input
                      type="hidden"
                      name="FDICode"
                      value={paymentData?.fdiCode ?? ''}
                    />
                    <input
                      type="hidden"
                      name="PolicyNumber"
                      value={paymentData?.policyNumber ?? ''}
                    />
                    <input
                      type="hidden"
                      name="ClientID"
                      value={paymentData?.clientID ?? ''}
                    />
                    <input
                      type="hidden"
                      name="ID"
                      value={paymentData?.ID ?? ''}
                    />
                  </form>
                </>
              )}
            </View>
            <View className="mt-4" style={style.horizontalDivider} />
            <View
              style={{
                flex: 1,
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
              className="mt-4 w-full"
            >
              {billingAndPremiumData.map((data, index) => {
                return (
                  <View
                    key={`${data.title}-${index}`}
                    style={{
                      paddingBottom: 20,
                    }}
                    className="w-1/2 sm:w-1/2 md:w-1/3 lg:w-1/4"
                  >
                    <InfoWrapper
                      title={data.title}
                      subTitle
                      information={data.value ?? '-'}
                      infoTextStyle={{
                        fontWeight: '500',
                      }}
                    />
                  </View>
                );
              })}
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              {allowLatestStatement && (
                <LatestStatement
                  onPress={() => {
                    trackCustomEvent(
                      analyticsTags.CaptureLatestStatementspolicyDetailspg,
                    );
                  }}
                  accessToken={accessToken}
                  policyNumber={policyNumber}
                  pageName={'POLICY DETAILS'}
                />
              )}
            </View>
          </View>
        </View>
      </View>
      <MakePaymentModal
        onSubmit={(selectedPayments) => {
          setIsLoading(true);
          trackCustomEvent(analyticsTags.CaptureMakeAPaymentSubmitButtonTags);

          dispatch({
            type: GET_PAYMENTS_DATA,
            payload: {
              token: policyData?.token,
              policyNumber:
                policyData?.getPoliciesListData?.data?.policySummary?.account
                  ?.number,
              paymentTypes: getSelectedPaymentsCodes(selectedPayments) || [],
              isLoading: true,
            },
          });
          setModalVisible(false);
        }}
        onCloseAnalytics={sendAnalytics}
        isModalVisible={isModalVisible}
        setModalVisible={setModalVisible}
        payments={getPaymentTypes()}
      />
      <LegalDisclaimerModal
        isVisible={!!paymentEncryptedData?.data}
        onAgree={handleLegalDisclaimerAgree}
        isHomePage={false}
        onClose={() => {
          dispatch(resetPaymentsEncryptedData());
          setPaymentData(null);
          setIsLoading(false);
        }}
      />
      <ProcessingTroubleModal
        showModal={!!paymentEncryptedData?.error}
        onClose={() => {
          dispatch(resetPaymentsEncryptedData());
          setIsLoading(false);
          trackCustomEvent(
            analyticsTags.CaptureProcessingTroubleModalClosePolicyDetailsPage,
          );
        }}
      />
    </>
  );
};

export default BillingAndPremium;
